'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string>('readonly');
  const supabase = createClient();

  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('granted_at', { ascending: false })
        .limit(1)
        .single();

      if (!error && data?.role) {
        setUserRole(data.role);
        return;
      }

      // Fallback 1: check JWT metadata
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const metaRole =
        authUser?.user_metadata?.role ||
        authUser?.app_metadata?.role;

      if (metaRole) {
        setUserRole(metaRole);
        return;
      }

      // Fallback 2: if user_roles table is completely empty (no rows at all),
      // this is the owner/first user — grant super_admin so the app is usable
      const { count } = await supabase
        .from('user_roles')
        .select('id', { count: 'exact', head: true });

      if (count === 0) {
        // Attempt to auto-insert the role so future loads are instant
        await supabase.from('user_roles').insert({
          user_id: userId,
          role: 'super_admin',
          is_active: true,
        }).select().single();
        setUserRole('super_admin');
        return;
      }

      // Default: readonly if a role table exists but this user has no entry
      setUserRole('readonly');
    } catch {
      setUserRole('readonly');
    }
  };

  useEffect(() => {
    // Get initial session and refresh token if needed
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        // Attempt to refresh the token on startup to ensure it's valid
        const { data: refreshed } = await supabase.auth.refreshSession();
        const activeSession = refreshed?.session ?? session;
        setSession(activeSession);
        setUser(activeSession?.user ?? null);
        if (activeSession?.user) {
          fetchUserRole(activeSession.user.id);
        }
      } else {
        setSession(null);
        setUser(null);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
      } else {
        setUserRole('readonly');
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Google SSO Sign In
  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    userRole,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
